import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Person,
  AttachMoney,
  Assignment,
  CheckCircle,
} from '@mui/icons-material';
import { hostEarningsService, HostEarningsSummary } from '@/services/hostEarningsService';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const HostEarningsOverview: React.FC = () => {
  const [summary, setSummary] = useState<HostEarningsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await hostEarningsService.getEarningsSummary();
      console.log('Host earnings summary:', data);
      setSummary(data);
    } catch (err: any) {
      console.error('Error fetching host earnings summary:', err);
      const errorMessage = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || 'Failed to load earnings summary';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!summary) {
    return (
      <Alert severity="info">
        No earnings data available.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.lighter', borderLeft: 4, borderColor: 'success.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Earnings
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.totalEarnings)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'success.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Hosts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {summary.totalHosts}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {summary.activeHosts} active
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, color: 'info.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.lighter', borderLeft: 4, borderColor: 'warning.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pending Payouts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.pendingPayouts)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'warning.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.lighter', borderLeft: 4, borderColor: 'primary.main', height: '100%' }}>
            <CardContent sx={{ minHeight: 120 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Completed Payouts
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatCurrency(summary.completedPayouts)}
                  </Typography>
                  <Box sx={{ height: 20 }} /> {/* Spacer for alignment */}
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'primary.main', opacity: 0.6 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Average Earnings */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Average Earnings Per Host
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {formatCurrency(summary.averageEarningsPerHost)}
          </Typography>
        </CardContent>
      </Card>

      {/* Top Earning Hosts */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Top Earning Hosts
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>#</TableCell>
                  <TableCell>Host</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Total Earnings</TableCell>
                  <TableCell align="right">Available Balance</TableCell>
                  <TableCell align="right">Bookings</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.topEarningHosts && summary.topEarningHosts.length > 0 ? (
                  summary.topEarningHosts.map((host, index) => (
                    <TableRow key={host.hostId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {host.hostName}
                        </Typography>
                        {host.hostCode && (
                          <Typography variant="caption" color="text.secondary">
                            Code: {host.hostCode}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{host.hostEmail}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(host.totalEarnings)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                          {formatCurrency(host.availableBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{host.bookingCount}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={host.status}
                          size="small"
                          color={
                            host.status === 'active'
                              ? 'success'
                              : host.status === 'suspended'
                              ? 'error'
                              : 'default'
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No host earnings data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
